<template>
    <div class="widget-component">
        <v-container fluid>
            <v-container class="container-width">
                <v-row>
                    <v-col cols="12">
                        <v-card
                            :loading="loading"
                            :width="normalizedOptions.card.width"
                            :elevation="normalizedOptions.card.elevation"
                            class="mx-auto my-12"
                        >
                            <v-card-text>
                                <v-row align="center" class="mx-0">
                                    <v-col cols="12">
                                        <h1 class="mb-6 color-primary">{{ normalizedOptions.title }}</h1>
                                    </v-col>
                                    <v-col v-if="loading" cols="12">
                                        <p>Verifying your email address. Please wait...</p>
                                    </v-col>
                                    <v-col v-if="isVerified" cols="12">
                                        <v-alert type="success">
                                            Your email has been verified. You can now close this window.
                                        </v-alert>
                                    </v-col>
                                    <v-col v-if="errors" cols="12">
                                        <v-alert type="error">
                                            Sorry, your email could not be verified. {{ errors.message }}.
                                        </v-alert>
                                    </v-col>
                                    <v-col v-if="normalizedOptions.register.show" cols="12">
                                        <p>
                                            {{ normalizedOptions.register.text }}
                                            <a :href="registerUrl"
                                               :style="{ color: `${normalizedOptions.register.linkColour}` }"
                                            >
                                                {{ normalizedOptions.register.linkText }}
                                            </a>
                                        </p>
                                    </v-col>
                                    <v-col v-if="normalizedOptions.login.show" cols="12">
                                        <p>
                                            {{ normalizedOptions.login.text }}
                                            <a :href="loginUrl"
                                               :style="{ color: `${normalizedOptions.login.linkColour}` }"
                                            >
                                                {{ normalizedOptions.login.linkText }}
                                            </a>
                                        </p>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>
    </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from "@vue/composition-api";
import { merge, isInteger, isString } from "lodash";
import AuthenticationService from "../services/AuthenticationService";
import ProfileState from "../state/ProfileState";
import axios from "axios";

export default defineComponent({
    name: 'EmailVerification',
    components: {},
    props: {
        accessToken: {
            type: String
        },
        rootUrl: {
            type: String,
            required: true
        },
        apiUrl: {
            type: String,
            required: true
        },
        loginUrl: {
            type: String,
            required: true
        },
        registerUrl: {
            type: String,
            required: true
        },
        settings: {
            type: Object
        }
    },
    setup: function (props) {
        const loading = ref(true);
        const errors = ref(null);
        const isVerified = ref(false);
        const defaultSettings = ref({
            title: 'Email Verification',
            card: {
                width: 622,
                elevation: 5
            },
            register: {
                show: false,
                text: 'Need an account?',
                textColour: '#000',
                linkText: 'Sign up here',
                linkColour: '#000'
            },
            login: {
                show: false,
                text: 'Already have an account?',
                textColour: '#000',
                linkText: 'Login here',
                linkColour: '#000'
            },
            events: {
                redirectAfterLogin: null,
                redirectIfLoggedIn: null
            }
        });

        const normalizedOptions = computed(() => {
            return merge(defaultSettings.value, props.settings);
        });

        const userId = computed(() => {
            let paths = window.location.pathname.split('/');

            if (isValidUserId(paths[paths.length - 2])) {
                return paths[paths.length - 2];
            }
            return null;
        });

        const magicLinkToken = computed(() => {
            let paths = window.location.pathname.split('/');

            if (isValidToken(paths[paths.length - 1])) {
                return paths[paths.length - 1];
            }
            return null;
        });

        function getEvent(event) {
            return normalizedOptions.value?.events[event] ?? null;
        }

        function isValidUserId(userId) {
            return isInteger(parseInt(userId));
        }

        function isValidToken(token) {
            return (isString(token) && token.length === 64);
        }

        function hasValidParameters() {
            return (isValidUserId(userId.value) && isValidToken(magicLinkToken.value));
        }

        function setError(error) {
            loading.value = false;
            errors.value = (isString(error)) ? { message: error } : error;
        }

        function init() {
            if (props.accessToken) {
                handleVerifiedRequest('redirectIfLoggedIn');
                return;
            }
            if (!hasValidParameters()) {
                setError('Invalid request parameters');
                return;
            }
            verifyEmailToken();
        }

        function handleVerifiedRequest(eventCallback) {
            loading.value = false;
            let redirectUrl = getEvent(eventCallback);

            if (redirectUrl) {
                return window.location.href = redirectUrl;
            }
            isVerified.value = true;
        }

        function verifyEmailToken() {
            AuthenticationService.magicLinkAccessToken(userId.value, magicLinkToken.value)
                .then(response => loadProfile(response.data.accessToken))
                .catch(error => {
                    setError(error?.response?.data ?? error.message);
                });
        }

        function loadProfile(accessToken) {
            return new Promise((resolve, reject) => {
                let config = {
                    baseURL: props.apiUrl,
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };
                axios.get('/profile', config)
                     .then(response => {
                         ProfileState.update(response.data);
                         ProfileState.registerApiCallDate();
                         handleVerifiedRequest('redirectAfterLogin');
                     })
                     .catch(error => reject(error));
            });
        }

        onMounted(() => {
            init();
        });

        return {
            loading,
            errors,
            normalizedOptions,
            isVerified
        };
    }
});
</script>
